<template>
  <div>
    <LabeledSelect
      v-if="showModeMenu"
      v-model="mode"
      :append-inner-icon="isEditingExistingItem ? 'lock' : undefined"
      :disabled="isEditingExistingItem"
      :items="modeOptions"
      :mandatory="true"
      item-title="text"
      item-value="value"
      message="Select type"
    />

    <div
      v-if="isAutomaticMode && loadingOptions"
      class="d-flex justify-center pa-4"
    >
      <v-progress-circular
        color="primary"
        indeterminate
      />
      <span class="ml-2">{{ $bct('Loading options...') }}</span>
    </div>

    <template v-else-if="isAutomaticMode">
      <v-alert
        v-if="!isEditingExistingItem && invoiceLineItemOptions.length === 0"
        :text="noOptionsMessage"
        color="primary"
        type="error"
        tile
      />

      <LabeledAutocomplete
        v-if="!isEditingExistingItem"
        v-model="data.invoice_line_item_configuration_id"
        @update:model-value="applyDefaultAmountForConfiguredManualLineItem"
        :items="Array.isArray(invoiceLineItemOptions) ? invoiceLineItemOptions : []"
        :mandatory="true"
        data-testId="invoice-line-item-configuration"
        item-title="description"
        item-value="invoice_line_item_configuration_id"
        message="Description"
        clearable
      />

      <LabeledTextfield
        v-else
        :value="
          data.description || getConfigurationDescription(data.invoice_line_item_configuration_id)
        "
        append-inner-icon="lock"
        message="Description"
        disabled
      />

      <template v-if="isManualConfig">
        <LabeledCurrencyInput
          v-model="data.amount"
          :mandatory="true"
          data-testId="invoice-line-item-amount"
          message="Amount"
        />
      </template>

      <template v-else>
        <v-row>
          <v-col>
            <LabeledNumber
              v-model="data.quantity"
              :mandatory="true"
              message="Quantity"
            />
          </v-col>

          <v-col>
            <LabeledCurrencyInput
              :value="displayRate"
              append-inner-icon="lock"
              data-testId="invoice-line-item-rate"
              message="Rate"
              disabled
            />
          </v-col>
        </v-row>

        <div
          v-if="displayRateDescription"
          class="d-flex align-start pb-2 ml-3"
        >
          <v-icon
            :title="displayRateDescription"
            class="text-grey-darken-1 align-self-center mr-2"
            icon="info_outline"
            size="x-small"
          />
          <p class="text-grey-darken-2 fs-16">
            {{ displayRateDescription }}
          </p>
        </div>
      </template>
    </template>

    <template v-else>
      <LabeledCurrencyInput
        v-model="data.amount"
        :mandatory="true"
        message="Amount"
      />

      <LabeledTextfield
        v-model="data.description"
        :mandatory="true"
        message="Description"
      />
    </template>

    <!-- Shared fields for both modes -->
    <LabeledTextarea
      v-if="!props.subsidyAwardPeriodId"
      v-model="data.reason"
      :mandatory="true"
      :rows="3"
      message="Reason"
    />

    <LabeledAutocomplete
      v-if="fundingSources.length > 0"
      v-model="data.funding_source_id"
      :items="fundingSources"
      :mandatory="props.fundingSourceRequired"
      item-title="name"
      item-value="id"
      message="Funding source"
      clearable
    />
  </div>
</template>

<script setup>
import LabeledAutocomplete from '@/shared/components/form/LabeledAutocomplete.vue';
import LabeledCurrencyInput from '@/shared/components/form/LabeledCurrencyInput.vue';
import LabeledNumber from '@/shared/components/form/LabeledNumber.vue';
import LabeledSelect from '@/shared/components/form/LabeledSelect.vue';
import LabeledTextarea from '@/shared/components/form/LabeledTextarea.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import useInvoiceLineItemOptions from '@/shared/composables/useInvoiceLineItemOptions';

const $bct = inject('translator');

const props = defineProps({
  allowNegativeAmount: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object,
    required: true,
  },
  enableAutomaticMode: {
    type: Boolean,
    default: false,
  },
  fundingSources: {
    type: Array,
    default: () => [],
  },
  fundingSourceRequired: {
    type: Boolean,
    default: false,
  },
  invoice: {
    type: Object,
    default: null,
  },
  subsidyProgram: {
    type: Object,
    default: null,
  },
  subsidyAwardPeriodId: {
    type: String,
    default: null,
  },
});

const noOptionsMessage = computed(() => {
  const defaultNoOptionsError =
    'No line items available. Check that all data required for rate calculation is present.';
  return props.subsidyProgram?.no_invoice_line_item_options_found_message || defaultNoOptionsError;
});

const {
  loadingOptions,
  invoiceLineItemOptions,
  loadOptions,
  getSelectedOption,
  getDisplayRateDescription,
  getConfigurationDescription,
  clearOptions,
} = useInvoiceLineItemOptions();

// Set initial mode:
const isEditingExistingItem = computed(() => !!props.data?.id);
const canLoadOptions = computed(() => !!(props.invoice?.id || props.subsidyAwardPeriodId));
const showModeMenu = computed(() => {
  if (!props.enableAutomaticMode) return false;
  if (props.subsidyAwardPeriodId) return false;
  if (!canLoadOptions.value) return false;
  return true;
});
const initialMode = () => {
  if (props.subsidyAwardPeriodId) return 'automatic';
  if (!props.enableAutomaticMode) return 'manual';
  if (!canLoadOptions.value) return 'manual';
  if (!isEditingExistingItem.value) return 'manual';
  if (!props.data?.invoice_line_item_configuration_id) return 'manual';

  return 'automatic';
};
const mode = ref(initialMode());

const effectiveConfigType = computed(
  () =>
    props.data?.config_type ??
    getSelectedOption(props.data?.invoice_line_item_configuration_id)?.config_type ??
    null,
);

const displayRate = computed(() => {
  if (!isAutomaticMode.value) return null;
  if (props.data?.rate) return props.data.rate;
  if (!props.data?.invoice_line_item_configuration_id) return null;

  const selectedOption = getSelectedOption(props.data.invoice_line_item_configuration_id);
  return selectedOption?.rate || null;
});

const displayRateDescription = computed(() => {
  if (!isAutomaticMode.value) return;
  if (!props.data?.invoice_line_item_configuration_id) return;
  if (isManualConfig.value) return;

  return (
    getDisplayRateDescription(props.data.invoice_line_item_configuration_id) ||
    props.data?.rate_description
  );
});

const isAutomaticMode = computed(() => mode.value === 'automatic');
const isManualConfig = computed(() => effectiveConfigType.value === 'manual_amount');

const modeOptions = computed(() => {
  const options = [{ text: $bct('Manual'), value: 'manual' }];
  if (props.enableAutomaticMode) {
    options.push({ text: $bct('Automatic'), value: 'automatic' });
  }
  return options;
});

function isInvalidLineItemNumber(rawValue) {
  if (rawValue == null || rawValue === '') return true;

  const numeric = Number(rawValue);
  if (Number.isNaN(numeric) || numeric === 0) return true;
  if (!props.allowNegativeAmount && numeric < 0) return true;

  return false;
}

function isSaveDisabled() {
  const data = props.data;
  if (!data) return true;

  if (isAutomaticMode.value) {
    if (loadingOptions.value) return true;
    if (!data.invoice_line_item_configuration_id) return true;
    if (props.fundingSourceRequired && !data.funding_source_id) return true;
    if (!props.subsidyAwardPeriodId && !data.reason) return true;

    if (effectiveConfigType.value === 'manual_amount') {
      return isInvalidLineItemNumber(data.amount);
    } else if (effectiveConfigType.value === 'calculated_rate') {
      return isInvalidLineItemNumber(data.quantity);
    }
  }

  if (isInvalidLineItemNumber(data.amount)) return true;
  if (!data.description) return true;
  if (!props.subsidyAwardPeriodId && !data.reason) return true;
  if (props.fundingSourceRequired && !data.funding_source_id) return true;

  return false;
}

defineExpose({
  isSaveDisabled,
});

const load = async () => {
  if (!isEditingExistingItem.value) {
    if (mode.value === 'manual') {
      props.data.invoice_line_item_configuration_id = null;
      props.data.quantity = null;
      props.data.rate = null;
    } else if (mode.value === 'automatic') {
      props.data.amount = null;
      props.data.description = null;
    }
  }

  const shouldLoadOptions = isAutomaticMode.value && !isEditingExistingItem.value;
  if (shouldLoadOptions) {
    await loadOptions({
      subsidy_award_period_id: props.subsidyAwardPeriodId,
      invoice_id: props.invoice?.id,
    });
  } else {
    clearOptions();
  }
};

watch(mode, load, { immediate: true });

function applyDefaultAmountForConfiguredManualLineItem(configurationId) {
  const option = configurationId ? getSelectedOption(configurationId) : null;
  if (option?.config_type === 'manual_amount') {
    props.data.amount = option.default_amount;
    props.data.quantity = null;
  } else {
    props.data.amount = null;
  }
}
</script>
